/**
 * Hooks and helpers for SetupGuard state logic.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { isExemptPath } from './SetupGuard-shared'
import { listNumberingSeries } from '../services/document-numbering-service'
import { hasAnyFiscalPeriods, hasOpenCurrentPeriod } from '../services/fiscal-period-service'
import { getOrganization } from '../services/organization-service'
import { useLedgerContext } from '../stores/ledger-store'
import { useOrganization } from '../stores/organization-store'

export interface CompanyDetailsCheck {
  isComplete: boolean
  missingFields: string[]
}

export async function checkCompanyDetails(organizationId: string): Promise<CompanyDetailsCheck> {
  const orgRes = await getOrganization(organizationId)
  const org = orgRes.data
  if (!org) { return { isComplete: true, missingFields: [] } }

  const missing: string[] = []

  if (!org.entityId) {
    missing.push('Company profile not configured (name, address)')
  }

  if (org.defaultLedgerId) {
    try {
      const series = await listNumberingSeries(organizationId, org.defaultLedgerId)
      if (series.length === 0) {
        missing.push('Document numbering series (required for invoices and bills)')
      }
    } catch { /* don't block on error */ }
  } else {
    missing.push('Document numbering series (required for invoices and bills)')
  }

  return { isComplete: missing.length === 0, missingFields: missing }
}

export interface GuardState {
  isLoaded: boolean
  hasLedgers: boolean
  isCheckingPeriods: boolean
  hasPeriods: boolean | null
  hasCurrentPeriod: boolean | null
  isCheckingCompany: boolean
  companyDetails: CompanyDetailsCheck | null
  retriggerCheck: () => void
}

interface PeriodCheckSetters {
  setIsCheckingPeriods: (v: boolean) => void
  setHasPeriods: (v: boolean | null) => void
  setHasCurrentPeriod: (v: boolean | null) => void
  setHasChecked: (v: boolean) => void
}

async function runPeriodCheck(orgId: string, setters: PeriodCheckSetters): Promise<void> {
  setters.setIsCheckingPeriods(true)
  try {
    const hasFiscalPeriods = await hasAnyFiscalPeriods(orgId)
    setters.setHasPeriods(hasFiscalPeriods)
    if (!hasFiscalPeriods) {
      setters.setHasCurrentPeriod(false)
      setters.setHasChecked(true)
      return
    }
    const hasOpen = await hasOpenCurrentPeriod(orgId)
    setters.setHasCurrentPeriod(hasOpen)
    setters.setHasChecked(true)
  } catch {
    setters.setHasPeriods(true)
    setters.setHasCurrentPeriod(true)
    setters.setHasChecked(true)
  } finally {
    setters.setIsCheckingPeriods(false)
  }
}

interface CompanyCheckSetters {
  setIsCheckingCompany: (v: boolean) => void
  setCompanyDetails: (v: CompanyDetailsCheck | null) => void
}

async function runCompanyDetailsCheck(orgId: string, setters: CompanyCheckSetters): Promise<void> {
  setters.setIsCheckingCompany(true)
  try {
    const result = await checkCompanyDetails(orgId)
    setters.setCompanyDetails(result)
  } catch {
    setters.setCompanyDetails({ isComplete: true, missingFields: [] })
  } finally {
    setters.setIsCheckingCompany(false)
  }
}

interface GuardEffectInput {
  isLoaded: boolean
  ledgersLen: number
  organizationId: string
  isExempt: boolean
  hasChecked: boolean
  periodSetters: PeriodCheckSetters
  companySetter: (v: CompanyDetailsCheck | null) => void
}

function applyGuardSkip(input: Pick<GuardEffectInput, 'periodSetters' | 'companySetter'>): void {
  input.periodSetters.setHasPeriods(true)
  input.periodSetters.setHasCurrentPeriod(true)
  input.periodSetters.setIsCheckingPeriods(false)
  input.periodSetters.setHasChecked(true)
  input.companySetter({ isComplete: true, missingFields: [] })
}

interface PeriodsState {
  isCheckingPeriods: boolean
  setIsCheckingPeriods: React.Dispatch<React.SetStateAction<boolean>>
  hasPeriods: boolean | null
  setHasPeriods: React.Dispatch<React.SetStateAction<boolean | null>>
  hasCurrentPeriod: boolean | null
  setHasCurrentPeriod: React.Dispatch<React.SetStateAction<boolean | null>>
  hasChecked: boolean
  setHasChecked: React.Dispatch<React.SetStateAction<boolean>>
}

function usePeriodsState(): PeriodsState {
  const [isCheckingPeriods, setIsCheckingPeriods] = useState(false)
  const [hasPeriods, setHasPeriods] = useState<boolean | null>(true)
  const [hasCurrentPeriod, setHasCurrentPeriod] = useState<boolean | null>(true)
  const [hasChecked, setHasChecked] = useState(false)
  return {
    isCheckingPeriods, setIsCheckingPeriods,
    hasPeriods, setHasPeriods,
    hasCurrentPeriod, setHasCurrentPeriod,
    hasChecked, setHasChecked,
  }
}

interface CompanyState {
  isCheckingCompany: boolean
  setIsCheckingCompany: React.Dispatch<React.SetStateAction<boolean>>
  companyDetails: CompanyDetailsCheck | null
  setCompanyDetails: React.Dispatch<React.SetStateAction<CompanyDetailsCheck | null>>
}

function useCompanyState(): CompanyState {
  const [isCheckingCompany, setIsCheckingCompany] = useState(false)
  const [companyDetails, setCompanyDetails] = useState<CompanyDetailsCheck | null>(null)
  return { isCheckingCompany, setIsCheckingCompany, companyDetails, setCompanyDetails }
}

function useResetOnExempt(isExempt: boolean, periods: PeriodsState, company: CompanyState): void {
  const prevIsExemptRef = useRef(isExempt)
  useEffect(() => {
    const didExempt = prevIsExemptRef.current
    prevIsExemptRef.current = isExempt
    if (didExempt && !isExempt) {
      periods.setHasChecked(false)
      company.setCompanyDetails(null)
    }
  }, [isExempt, periods, company])
}

interface NavigateInput {
  isInitializing: boolean
  isLedgerStoreReady: boolean
  isExempt: boolean
  ledgersLen: number
  organizationId: string
  pathname: string
  navigate: ReturnType<typeof useNavigate>
}

function useNavigateToSetup(input: NavigateInput): void {
  const { isInitializing, isLedgerStoreReady, isExempt, ledgersLen, organizationId, pathname, navigate } = input
  useEffect(() => {
    if (isInitializing) { return }
    if (!isLedgerStoreReady) { return }
    if (!isExempt && ledgersLen === 0) {
      navigate('/setup', { replace: true })
    }
  }, [ledgersLen, isInitializing, isLedgerStoreReady, navigate, isExempt, organizationId, pathname])
}

interface MainEffectInput {
  isLoaded: boolean
  ledgersLen: number
  organizationId: string
  isExempt: boolean
  hasChecked: boolean
  checkPeriods: (orgId: string) => Promise<void>
  periods: PeriodsState
  company: CompanyState
}

function useMainGuardEffect(input: MainEffectInput): void {
  const { isLoaded, ledgersLen, organizationId, isExempt, hasChecked, checkPeriods, periods, company } = input
  const { setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setHasChecked } = periods
  const { setCompanyDetails } = company
  useEffect(() => {
    if (!isLoaded) { setHasChecked(false); return }
    if (ledgersLen === 0 || !organizationId || isExempt) {
      applyGuardSkip({
        periodSetters: { setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setHasChecked },
        companySetter: setCompanyDetails,
      })
      return
    }
    if (!hasChecked) { void checkPeriods(organizationId) }
  }, [ledgersLen, isLoaded, organizationId, isExempt, hasChecked, checkPeriods, setHasChecked, setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setCompanyDetails])
}

export function useSetupGuardState(): GuardState {
  const { ledgers, isLoaded, isLoading } = useLedgerContext()
  const { organizationId, isInitializing } = useOrganization()
  const navigate = useNavigate()
  const location = useLocation()
  const periods = usePeriodsState()
  const company = useCompanyState()
  const isExempt = isExemptPath(location.pathname)
  const isLedgerStoreReady = !isInitializing && isLoaded && !isLoading

  useResetOnExempt(isExempt, periods, company)
  useNavigateToSetup({
    isInitializing, isLedgerStoreReady, isExempt,
    ledgersLen: ledgers.length, organizationId, pathname: location.pathname, navigate,
  })

  const { setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setHasChecked } = periods
  const { setIsCheckingCompany, setCompanyDetails } = company

  const checkPeriods = useCallback(async (orgId: string): Promise<void> => {
    await runPeriodCheck(orgId, { setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setHasChecked })
  }, [setIsCheckingPeriods, setHasPeriods, setHasCurrentPeriod, setHasChecked])

  const runCompanyCheck = useCallback(async (orgId: string): Promise<void> => {
    await runCompanyDetailsCheck(orgId, { setIsCheckingCompany, setCompanyDetails })
  }, [setIsCheckingCompany, setCompanyDetails])

  const retriggerCheck = useCallback((): void => {
    setHasChecked(false)
    setCompanyDetails(null)
  }, [setHasChecked, setCompanyDetails])

  useMainGuardEffect({
    isLoaded, ledgersLen: ledgers.length, organizationId, isExempt,
    hasChecked: periods.hasChecked, checkPeriods, periods, company,
  })

  useEffect(() => {
    if (!isLoaded || !organizationId || isExempt || company.companyDetails !== null) { return }
    void runCompanyCheck(organizationId)
  }, [isLoaded, organizationId, isExempt, company.companyDetails, runCompanyCheck])

  return {
    isLoaded,
    hasLedgers: ledgers.length > 0,
    isCheckingPeriods: periods.isCheckingPeriods,
    hasPeriods: periods.hasPeriods,
    hasCurrentPeriod: periods.hasCurrentPeriod,
    isCheckingCompany: company.isCheckingCompany,
    companyDetails: company.companyDetails,
    retriggerCheck,
  }
}
